import React, { Component } from "react";
import { Spin, Select, Tag, Modal } from "antd";
import Swal from "sweetalert2";
import { Configuration } from "../Configuration";
import "antd/dist/antd.css";

const { Option } = Select;
const API_BASE = Configuration.SYSTEM_SETTINGS.API_URL;
const DB = "IMAGEDB";

function mFetch(method, path, body) {
  const token = localStorage.getItem("authToken1") || "";
  const opts = {
    method,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
  };
  if (body !== undefined) opts.body = JSON.stringify(body);
  return fetch(API_BASE + path, opts).then(r => r.json());
}

export default class frmMasterDataFiller extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // portal config from DB
      portals: [],
      configLoading: true,

      // step 1 — setup
      selectedPortalId: null,
      masterFile: null,
      masterHeaderRow: 1,
      templateFile: null,
      templateHeaderRow: 1,
      templateDataStartRow: 2,

      // step 2 — mapping
      step: "setup",
      masterCols: [],
      inputCols: [],
      mapping: {},          // { master_col: input_col | "" }
      sampleValues: {},     // { master_col: [val, ...] }
      masterRowCount: 0,
      appendUnmatched: true,

      // manage portals modal
      manageOpen: false,
      editingPortalId: null,
      newPortalName: "",
      addColName: "",
      addColMandatory: true,
      editHR: {},           // { portalId: { inputHeaderRow, inputDataStartRow, masterHeaderRowDefault } }

      loading: false,
    };
    this.masterFileRef = React.createRef();
    this.templateFileRef = React.createRef();
    this._renameTimers = {};
  }

  componentDidMount() { this.loadConfig(); }

  // ── Config ────────────────────────────────────────────────────────────────

  loadConfig = async () => {
    this.setState({ configLoading: true });
    try {
      const d = await mFetch("GET", "api/master-portal/config");
      if (d.status === "ok") this.setState({ portals: d.portals || [], configLoading: false });
    } catch { this.setState({ configLoading: false }); }
  };

  // ── Portal management ─────────────────────────────────────────────────────

  addPortal = async () => {
    const name = this.state.newPortalName.trim();
    if (!name) return;
    const key = name.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
    const res = await mFetch("POST", "api/master-portal/portal", { key, name });
    if (res.status === "ok") {
      this.setState({ newPortalName: "", editingPortalId: res.portalId });
      await this.loadConfig();
    } else {
      Swal.fire({ icon: "error", title: res.message || "Failed", timer: 2000, showConfirmButton: false });
    }
  };

  deletePortal = async (portalId, name) => {
    const r = await Swal.fire({
      icon: "warning", title: `Delete "${name}"?`,
      text: "All columns and the saved template will be removed.",
      showCancelButton: true, confirmButtonText: "Delete", confirmButtonColor: "#ff4d4f",
    });
    if (!r.isConfirmed) return;
    await mFetch("DELETE", `api/master-portal/portal/${portalId}`);
    await this.loadConfig();
    if (this.state.editingPortalId === portalId) this.setState({ editingPortalId: null });
    if (this.state.selectedPortalId === portalId) this.setState({ selectedPortalId: null, step: "setup" });
  };

  savePortalSettings = async (portalId) => {
    const { portals, editHR } = this.state;
    const p = portals.find(x => x.portalId === portalId);
    if (!p) return;
    const hr = editHR[portalId] || {};
    await mFetch("PUT", `api/master-portal/portal/${portalId}`, {
      name: p.portalName,
      inputHeaderRow:        hr.inputHeaderRow        ?? p.inputHeaderRow,
      inputDataStartRow:     hr.inputDataStartRow     ?? p.inputDataStartRow,
      masterHeaderRowDefault: hr.masterHeaderRowDefault ?? p.masterHeaderRowDefault,
    });
    await this.loadConfig();
    Swal.fire({ icon: "success", title: "Saved", timer: 900, showConfirmButton: false });
  };

  uploadTemplate = async (portalId, file) => {
    const form = new FormData();
    form.append("template", file);
    const token = localStorage.getItem("authToken1") || "";
    const res = await fetch(`${API_BASE}api/master-portal/upload-template/${portalId}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    }).then(r => r.json());

    if (res.status !== "ok") {
      Swal.fire({ icon: "error", title: "Upload failed", text: res.message, timer: 2000, showConfirmButton: false });
      return;
    }
    await this.loadConfig();

    const cols = res.columns || [];
    if (cols.length) {
      const p = this.state.portals.find(x => x.portalId === portalId);
      const existing = new Set((p?.columns || []).map(c => c.name.toLowerCase()));
      const toAdd = cols.filter(c => c && !existing.has(c.toLowerCase()));
      if (toAdd.length) {
        const r2 = await Swal.fire({
          icon: "question", title: "Auto-add columns?",
          html: `<div style="font-size:13px">${toAdd.length} column(s) found in template.<br>Add them to the portal config automatically?</div>`,
          showCancelButton: true, confirmButtonText: "Yes, add all", cancelButtonText: "Skip",
        });
        if (r2.isConfirmed) {
          const base = (p?.columns || []).length;
          for (let i = 0; i < toAdd.length; i++) {
            await mFetch("POST", "api/master-portal/column", {
              portalId, name: toAdd[i], isMandatory: false, sortOrder: base + i,
            });
          }
          await this.loadConfig();
        }
      }
    }
    Swal.fire({ icon: "success", title: "Template saved", timer: 1200, showConfirmButton: false });
  };

  addColumn = async (portalId) => {
    const { addColName, addColMandatory, portals } = this.state;
    const name = addColName.trim();
    if (!name) return;
    const p = portals.find(x => x.portalId === portalId);
    if (p?.columns.some(c => c.name.toLowerCase() === name.toLowerCase())) {
      Swal.fire({ icon: "warning", title: "Column already exists", timer: 1300, showConfirmButton: false });
      return;
    }
    const res = await mFetch("POST", "api/master-portal/column", {
      portalId, name, isMandatory: addColMandatory, sortOrder: (p?.columns.length || 0),
    });
    if (res.status === "ok") {
      this.setState({ addColName: "" });
      await this.loadConfig();
    }
  };

  toggleMandatory = async (colId, cur) => {
    await mFetch("PUT", `api/master-portal/column/${colId}`, { isMandatory: !cur });
    await this.loadConfig();
  };

  deleteColumn = async (colId) => {
    await mFetch("DELETE", `api/master-portal/column/${colId}`);
    await this.loadConfig();
  };

  // ── Analyze ───────────────────────────────────────────────────────────────

  analyze = async () => {
    const { selectedPortalId, masterFile, masterHeaderRow, templateFile, templateHeaderRow } = this.state;
    if (!masterFile) { Swal.fire("Warning", "Please upload a master data file first.", "warning"); return; }
    if (!selectedPortalId && !templateFile) { Swal.fire("Warning", "Select a portal or upload an input template.", "warning"); return; }

    const token = localStorage.getItem("authToken1") || "";
    this.setState({ loading: true });
    try {
      const form = new FormData();
      form.append("master_file", masterFile);
      form.append("master_header_row", masterHeaderRow);
      if (selectedPortalId) form.append("portal_id", selectedPortalId);
      if (templateFile) {
        form.append("template_file", templateFile);
        form.append("template_header_row", templateHeaderRow);
      }
      const res = await fetch(`${API_BASE}api/master-data/analyze?db=${DB}`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: form,
      }).then(r => r.json());
      if (res.error) throw new Error(res.error);

      // Build initial mapping: master_col → best input_col or ""
      const mapping = {};
      (res.master_cols || []).forEach(mc => { mapping[mc] = res.suggested?.[mc] || ""; });

      this.setState({
        masterCols: res.master_cols || [],
        inputCols:  res.input_cols  || [],
        mapping,
        sampleValues:   res.samples || {},
        masterRowCount: res.master_row_count || 0,
        step: "mapping",
        loading: false,
      });
    } catch (e) {
      this.setState({ loading: false });
      Swal.fire("Error", e.message, "error");
    }
  };

  // ── Generate ──────────────────────────────────────────────────────────────

  generateOutput = async () => {
    const {
      selectedPortalId, masterFile, masterHeaderRow,
      mapping, appendUnmatched,
      templateFile, templateHeaderRow, templateDataStartRow,
      portals,
    } = this.state;
    const token = localStorage.getItem("authToken1") || "";
    this.setState({ loading: true });
    try {
      const form = new FormData();
      form.append("master_file", masterFile);
      form.append("master_header_row", masterHeaderRow);
      form.append("mapping", JSON.stringify(mapping));
      form.append("append_unmatched", appendUnmatched ? "true" : "false");
      if (selectedPortalId) form.append("portal_id", selectedPortalId);
      if (templateFile) {
        form.append("template_file", templateFile);
        form.append("template_header_row", templateHeaderRow);
        form.append("template_data_start_row", templateDataStartRow);
      }
      const res = await fetch(`${API_BASE}api/master-data/fill?db=${DB}`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Fill failed");
      }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      const portal = portals.find(p => p.portalId === selectedPortalId);
      a.download = `${portal?.portalName || "Output"}_FilledTemplate.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      this.setState({ loading: false });
      Swal.fire({ icon: "success", title: "Done!", text: "Output file downloaded.", timer: 2000, showConfirmButton: false });
    } catch (e) {
      this.setState({ loading: false });
      Swal.fire("Error", e.message, "error");
    }
  };

  // ── Render: Manage Portals modal ──────────────────────────────────────────

  renderManageModal() {
    const { manageOpen, portals, newPortalName, editingPortalId, addColName, addColMandatory, editHR } = this.state;
    const ep = portals.find(p => p.portalId === editingPortalId);
    const hr = editHR[editingPortalId] || {};

    return (
      <Modal
        title="Manage Portals"
        visible={manageOpen}
        onCancel={() => this.setState({ manageOpen: false })}
        footer={null}
        width={740}
        bodyStyle={{ padding: 16 }}
      >
        {/* Add portal */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <input
            className="form-control" placeholder="New portal name…"
            style={{ flex: 1 }}
            value={newPortalName}
            onChange={e => this.setState({ newPortalName: e.target.value })}
            onKeyDown={e => e.key === "Enter" && this.addPortal()}
          />
          <button className="btn btn-primary btn-sm" onClick={this.addPortal}>+ Add Portal</button>
        </div>

        <div style={{ display: "flex", gap: 14, minHeight: 360 }}>
          {/* Left: portal list */}
          <div style={{ width: 190, borderRight: "1px solid #e8e8e8", paddingRight: 12 }}>
            {portals.map(p => (
              <div
                key={p.portalId}
                onClick={() => this.setState({ editingPortalId: p.portalId })}
                style={{
                  padding: "6px 10px", marginBottom: 4, borderRadius: 4, cursor: "pointer",
                  background: editingPortalId === p.portalId ? "#e6f7ff" : "#fafafa",
                  border: `1px solid ${editingPortalId === p.portalId ? "#1890ff" : "#e8e8e8"}`,
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                }}
              >
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{p.portalName}</div>
                  {p.hasTemplate
                    ? <div style={{ fontSize: 10, color: "#52c41a" }}>✓ template</div>
                    : <div style={{ fontSize: 10, color: "#faad14" }}>no template</div>}
                </div>
                <span style={{ color: "#ff4d4f", fontSize: 13, cursor: "pointer" }}
                  onClick={e => { e.stopPropagation(); this.deletePortal(p.portalId, p.portalName); }}>✕</span>
              </div>
            ))}
            {!portals.length && <div style={{ fontSize: 12, color: "#aaa", padding: 8 }}>No portals yet.</div>}
          </div>

          {/* Right: edit panel */}
          {ep ? (
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>{ep.portalName}</div>

              {/* Row config */}
              <div style={{ display: "flex", gap: 10, marginBottom: 14, alignItems: "flex-end", flexWrap: "wrap" }}>
                {[
                  { label: "Input Header Row",  field: "inputHeaderRow",        def: ep.inputHeaderRow },
                  { label: "Data Start Row",     field: "inputDataStartRow",     def: ep.inputDataStartRow },
                  { label: "Master Header Row",  field: "masterHeaderRowDefault", def: ep.masterHeaderRowDefault },
                ].map(({ label, field, def }) => (
                  <div key={field} style={{ fontSize: 12 }}>
                    <div style={{ marginBottom: 3 }}>{label}</div>
                    <input
                      type="number" min="1" max="20" className="form-control input-sm" style={{ width: 62 }}
                      value={hr[field] !== undefined ? hr[field] : (def || 1)}
                      onChange={e => this.setState(s => ({
                        editHR: { ...s.editHR, [editingPortalId]: { ...s.editHR[editingPortalId], [field]: parseInt(e.target.value) || 1 } },
                      }))}
                    />
                  </div>
                ))}
                <button className="btn btn-xs btn-success" onClick={() => this.savePortalSettings(ep.portalId)}>Save Rows</button>
              </div>

              {/* Template upload */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, marginBottom: 5 }}>
                  Input Template:{" "}
                  {ep.hasTemplate
                    ? <span style={{ color: "#52c41a" }}>✓ {ep.templateFilename}</span>
                    : <span style={{ color: "#faad14" }}>Not uploaded</span>}
                </div>
                <input type="file" accept=".xlsx,.xlsm,.xls" style={{ display: "none" }}
                  id={`tmpl-${ep.portalId}`}
                  onChange={e => e.target.files[0] && this.uploadTemplate(ep.portalId, e.target.files[0])} />
                <label htmlFor={`tmpl-${ep.portalId}`} className="btn btn-xs btn-default" style={{ cursor: "pointer" }}>
                  {ep.hasTemplate ? "Replace Template" : "Upload Template"}
                </label>
              </div>

              {/* Column list */}
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Output Columns</div>
              <div style={{ maxHeight: 160, overflowY: "auto", border: "1px solid #f0f0f0", borderRadius: 4, marginBottom: 10 }}>
                {ep.columns.map(c => (
                  <div key={c.id} style={{ display: "flex", alignItems: "center", padding: "4px 10px", gap: 8, borderBottom: "1px solid #f9f9f9", fontSize: 12 }}>
                    <span style={{ flex: 1 }}>{c.name}</span>
                    <Tag
                      color={c.isMandatory ? "red" : "default"}
                      style={{ cursor: "pointer", fontSize: 10, marginRight: 0 }}
                      onClick={() => this.toggleMandatory(c.id, c.isMandatory)}
                    >{c.isMandatory ? "Mandatory" : "Optional"}</Tag>
                    <span style={{ color: "#ff4d4f", cursor: "pointer", fontSize: 13 }} onClick={() => this.deleteColumn(c.id)}>✕</span>
                  </div>
                ))}
                {!ep.columns.length && <div style={{ color: "#aaa", fontSize: 12, padding: "8px 10px" }}>No columns. Upload template to auto-populate, or add manually.</div>}
              </div>

              {/* Add column */}
              <div style={{ display: "flex", gap: 6 }}>
                <input className="form-control input-sm" placeholder="Column name…" style={{ flex: 1 }}
                  value={addColName}
                  onChange={e => this.setState({ addColName: e.target.value })}
                  onKeyDown={e => e.key === "Enter" && this.addColumn(ep.portalId)} />
                <select className="form-control input-sm" style={{ width: 110 }}
                  value={addColMandatory ? "mandatory" : "optional"}
                  onChange={e => this.setState({ addColMandatory: e.target.value === "mandatory" })}>
                  <option value="mandatory">Mandatory</option>
                  <option value="optional">Optional</option>
                </select>
                <button className="btn btn-xs btn-primary" onClick={() => this.addColumn(ep.portalId)}>Add</button>
              </div>
            </div>
          ) : (
            <div style={{ flex: 1, color: "#aaa", fontSize: 13, paddingTop: 30, textAlign: "center" }}>
              Select a portal to edit its settings and columns.
            </div>
          )}
        </div>
      </Modal>
    );
  }

  // ── Render: Step 1 — Setup ────────────────────────────────────────────────

  renderSetup() {
    const { portals, selectedPortalId, masterFile, masterHeaderRow, templateFile, templateHeaderRow, templateDataStartRow, configLoading } = this.state;
    const portal = portals.find(p => p.portalId === selectedPortalId);
    const needsManualTemplate = !selectedPortalId || (portal && !portal.hasTemplate);
    const canAnalyze = !!masterFile && (selectedPortalId || !!templateFile);

    return (
      <div className="box-body">
        <div style={{ maxWidth: 660, margin: "0 auto" }}>

          {/* Portal selector */}
          <div style={{ marginBottom: 18 }}>
            <label style={{ fontWeight: 600, display: "block", marginBottom: 6 }}>Portal (optional)</label>
            <Select
              style={{ width: "100%" }}
              placeholder="Select a configured portal…"
              allowClear
              loading={configLoading}
              value={selectedPortalId || undefined}
              onChange={v => this.setState({ selectedPortalId: v || null })}
            >
              {portals.map(p => (
                <Option key={p.portalId} value={p.portalId}>
                  {p.portalName}
                  {p.hasTemplate
                    ? <span style={{ color: "#52c41a", fontSize: 11, marginLeft: 6 }}>✓ template saved</span>
                    : <span style={{ color: "#faad14", fontSize: 11, marginLeft: 6 }}>no template</span>}
                </Option>
              ))}
            </Select>
            <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>
              Leave blank to map manually by uploading both template + master file.
            </div>
          </div>

          {/* Portal template status */}
          {portal?.hasTemplate && (
            <div style={{ marginBottom: 18, padding: "8px 14px", background: "#f6ffed", border: "1px solid #b7eb8f", borderRadius: 6, fontSize: 12 }}>
              ✓ Template: <b>{portal.templateFilename}</b>
              &nbsp;· header row {portal.inputHeaderRow} · data from row {portal.inputDataStartRow}
            </div>
          )}

          {/* Manual template upload */}
          {needsManualTemplate && (
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, display: "block", marginBottom: 6 }}>
                Input Template File
                {selectedPortalId && !portal?.hasTemplate && (
                  <span style={{ color: "#faad14", fontWeight: 400, fontSize: 11, marginLeft: 8 }}>
                    No template saved for this portal — upload manually or add one via Manage Portals
                  </span>
                )}
              </label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <input type="file" ref={this.templateFileRef} accept=".xlsx,.xlsm,.xls" style={{ display: "none" }}
                  onChange={e => this.setState({ templateFile: e.target.files[0] || null })} />
                <button className="btn btn-sm btn-default" onClick={() => this.templateFileRef.current.click()}>Choose Template</button>
                {templateFile && <span style={{ fontSize: 12, color: "#52c41a" }}>✓ {templateFile.name}</span>}
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                  Header row:
                  <input type="number" min="1" max="30" className="form-control input-sm" style={{ width: 55 }}
                    value={templateHeaderRow}
                    onChange={e => this.setState({ templateHeaderRow: parseInt(e.target.value) || 1 })} />
                  Data from row:
                  <input type="number" min="1" max="30" className="form-control input-sm" style={{ width: 55 }}
                    value={templateDataStartRow}
                    onChange={e => this.setState({ templateDataStartRow: parseInt(e.target.value) || 2 })} />
                </div>
              </div>
            </div>
          )}

          {/* Master data file */}
          <div style={{ marginBottom: 24 }}>
            <label style={{ fontWeight: 600, display: "block", marginBottom: 6 }}>Master Data File</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <input type="file" ref={this.masterFileRef} accept=".xlsx,.xlsm,.xls,.xlsb" style={{ display: "none" }}
                onChange={e => this.setState({ masterFile: e.target.files[0] || null })} />
              <button className="btn btn-sm btn-default" onClick={() => this.masterFileRef.current.click()}>Choose Master File</button>
              {masterFile && <span style={{ fontSize: 12, color: "#52c41a" }}>✓ {masterFile.name}</span>}
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                Header row:
                <input type="number" min="1" max="30" className="form-control input-sm" style={{ width: 55 }}
                  value={masterHeaderRow}
                  onChange={e => this.setState({ masterHeaderRow: parseInt(e.target.value) || 1 })} />
              </div>
            </div>
          </div>

          <div style={{ textAlign: "center" }}>
            <button
              className="btn btn-primary btn-lg"
              onClick={this.analyze}
              disabled={!canAnalyze}
            >
              Analyze &amp; Map Columns
            </button>
            {!canAnalyze && (
              <p style={{ fontSize: 12, color: "#aaa", marginTop: 8 }}>
                Upload a master data file{!selectedPortalId ? " and a template (or select a portal)" : ""} to continue.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Render: Step 2 — Mapping ──────────────────────────────────────────────

  renderMapping() {
    const { masterCols, inputCols, mapping, sampleValues, masterRowCount, appendUnmatched, portals, selectedPortalId } = this.state;
    const portal = portals.find(p => p.portalId === selectedPortalId);
    const mandatorySet = new Set((portal?.columns || []).filter(c => c.isMandatory).map(c => c.name));

    // Reverse map: input_col → master_col
    const reverseMap: Record<string, string> = {};
    Object.entries(mapping).forEach(([mc, ic]) => { if (ic) reverseMap[ic] = mc; });

    const mappedMasterSet = new Set(Object.keys(mapping).filter(k => mapping[k]));
    const unmappedMaster  = masterCols.filter(mc => !mappedMasterSet.has(mc));

    const mappedCount = Object.values(mapping).filter(Boolean).length;

    return (
      <div className="box-body">
        {/* Sub-header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div>
            <span style={{ fontSize: 14, fontWeight: 600 }}>
              Column Mapping{portal ? ` — ${portal.portalName}` : " — Manual Mode"}
            </span>
            <span style={{ fontSize: 12, color: "#888", marginLeft: 12 }}>
              {masterRowCount} data row{masterRowCount !== 1 ? "s" : ""} · {masterCols.length} master columns
            </span>
          </div>
          <button className="btn btn-sm btn-default"
            onClick={() => this.setState({ step: "setup", masterCols: [], inputCols: [], mapping: {} })}>
            ← Back
          </button>
        </div>

        {/* Mapping table */}
        <div style={{ overflowX: "auto", marginBottom: 14 }}>
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#fafafa", borderBottom: "2px solid #e0e0e0" }}>
                <th style={{ padding: "8px 10px", textAlign: "left", minWidth: 180 }}>Input Template Column</th>
                <th style={{ padding: "8px 10px", textAlign: "center", width: 90 }}>Type</th>
                <th style={{ padding: "8px 10px", textAlign: "left", minWidth: 200 }}>← Master Data Column</th>
                <th style={{ padding: "8px 10px", textAlign: "left", color: "#999", minWidth: 160 }}>Sample Values</th>
              </tr>
            </thead>
            <tbody>
              {inputCols.map((ic, idx) => {
                const mappedMC = reverseMap[ic] || "";
                const isMand   = mandatorySet.has(ic);
                const samples  = mappedMC ? (sampleValues[mappedMC] || []) : [];
                const isMissing = isMand && !mappedMC;
                return (
                  <tr key={ic} style={{ borderBottom: "1px solid #f0f0f0", background: isMissing ? "#fff2f0" : idx % 2 === 0 ? "#fff" : "#fafafa" }}>
                    <td style={{ padding: "6px 10px", fontWeight: isMand ? 600 : 400 }}>
                      {ic}
                      {isMissing && <span style={{ color: "#ff4d4f", fontSize: 10, marginLeft: 6 }}>⚠ required</span>}
                    </td>
                    <td style={{ padding: "6px 10px", textAlign: "center" }}>
                      <Tag color={isMand ? "red" : "default"} style={{ fontSize: 10 }}>
                        {isMand ? "Required" : "Optional"}
                      </Tag>
                    </td>
                    <td style={{ padding: "4px 6px" }}>
                      <select
                        className="form-control input-sm"
                        value={mappedMC}
                        style={{ fontSize: 12 }}
                        onChange={e => {
                          const newMC = e.target.value;
                          const m = { ...mapping };
                          // Remove old assignment for this input col
                          Object.keys(m).forEach(k => { if (m[k] === ic) m[k] = ""; });
                          if (newMC) m[newMC] = ic;
                          this.setState({ mapping: m });
                        }}
                      >
                        <option value="">— not mapped —</option>
                        {masterCols.map(mc => (
                          <option key={mc} value={mc} disabled={!!mapping[mc] && mapping[mc] !== ic}>
                            {mc}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td style={{ padding: "6px 10px", fontSize: 11, color: "#888" }}>
                      {samples.slice(0, 2).join(", ")}
                    </td>
                  </tr>
                );
              })}
              {!inputCols.length && (
                <tr><td colSpan={4} style={{ padding: 20, textAlign: "center", color: "#aaa" }}>
                  No input template columns found. Ensure the portal has a saved template with the correct header row configured.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Unmatched master columns */}
        {unmappedMaster.length > 0 && (
          <div style={{ marginBottom: 14, padding: "10px 14px", background: "#fffbe6", border: "1px solid #ffe58f", borderRadius: 6 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>
                {unmappedMaster.length} unmatched master column{unmappedMaster.length !== 1 ? "s" : ""}
              </span>
              <label style={{ fontSize: 12, cursor: "pointer", margin: 0 }}>
                <input type="checkbox" checked={appendUnmatched}
                  onChange={e => this.setState({ appendUnmatched: e.target.checked })}
                  style={{ marginRight: 5 }} />
                Append to output
              </label>
            </div>
            <div style={{ fontSize: 12, color: "#666", maxHeight: 70, overflowY: "auto" }}>
              {unmappedMaster.join(" · ")}
            </div>
          </div>
        )}

        {/* Generate */}
        <div style={{ textAlign: "center", marginTop: 10 }}>
          <button className="btn btn-success btn-lg" onClick={this.generateOutput}>
            Generate Output
          </button>
          <p style={{ fontSize: 12, color: "#888", marginTop: 8 }}>
            {mappedCount} column{mappedCount !== 1 ? "s" : ""} mapped
            {appendUnmatched && unmappedMaster.length > 0 ? ` + ${unmappedMaster.length} unmatched appended at end` : ""}
          </p>
        </div>
      </div>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────

  render() {
    const { loading, manageOpen, step } = this.state;
    return (
      <div className="content-wrapper">
        {loading && (
          <div className="loader">
            <div className="es-spinner"><Spin size="large" /></div>
          </div>
        )}
        <section className="content">
          <div className="box box-success" style={{ marginTop: "-13px" }}>
            <div className="box-header with-border" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '12px 20px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, color: '#aaa', marginBottom: 3 }}>
                  Dashboard &gt; Image Tools &gt; Master Data Filler
                </div>
                <h3 className="box-title" style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
                  Master Data Filler
                </h3>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4, flexWrap: 'wrap' }}>
                <button className="btn btn-sm btn-default" onClick={() => this.setState({ manageOpen: true })}>
                  ⚙ Manage Portals
                </button>
              </div>
            </div>

            {/* How-to banner */}
            <div style={{ background: "#fffbe6", borderBottom: "1px solid #ffe58f", padding: "8px 16px", fontSize: 12, color: "#666" }}>
              <b>How to use:</b> Use <b>⚙ Manage Portals</b> to add portals, upload their input templates, and define mandatory/optional columns →
              Select a portal (or Manual mode) → Upload your master data file → Adjust column mapping → <b>Generate Output</b> to download the filled template.
            </div>

            {step === "setup"   && this.renderSetup()}
            {step === "mapping" && this.renderMapping()}
          </div>
        </section>
        {this.renderManageModal()}
      </div>
    );
  }
}
